import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import IndicatorCard from '../components/dashboard/IndicatorCard';
import ChartContainer from '../components/dashboard/ChartContainer';
import RecentActivities from '../components/dashboard/RecentActivities';
import AlertsPanel from '../components/dashboard/AlertsPanel';
import QuickActions from '../components/dashboard/QuickActions';
import { supabase, getCurrentUserId } from '../lib/supabase';
import type { FinancialIndicator } from '../types/dashboard';

const defaultChartData = {
  labels: [],
  datasets: []
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [indicators, setIndicators] = useState<FinancialIndicator[]>([]);
  const [salesData, setSalesData] = useState<any>(defaultChartData);
  const [financialData, setFinancialData] = useState<any>(defaultChartData);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Load previous period data for trend calculation
      const previousPeriodStart = new Date();
      previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
      const currentPeriodStart = new Date();
      currentPeriodStart.setDate(1);

      // Load sales data with trends
      const { data: currentSalesData } = await supabase
        .from('sales')
        .select('sum:total::numeric')
        .eq('user_id', userId)
        .gte('created_at', currentPeriodStart.toISOString());

      const { data: previousSalesData } = await supabase
        .from('sales')
        .select('sum:total::numeric')
        .eq('user_id', userId)
        .gte('created_at', previousPeriodStart.toISOString())
        .lt('created_at', currentPeriodStart.toISOString());

      const currentSalesTotal = currentSalesData?.[0]?.sum || 0;
      const previousSalesTotal = previousSalesData?.[0]?.sum || 0;
      const salesTrend = previousSalesTotal > 0 
        ? ((currentSalesTotal - previousSalesTotal) / previousSalesTotal) * 100
        : 0;

      // Load receivables with trends
      const { data: currentReceivablesData } = await supabase
        .from('transactions')
        .select('sum:amount::numeric')
        .eq('type', 'income')
        .eq('status', 'pending')
        .eq('user_id', userId)
        .gte('created_at', currentPeriodStart.toISOString());

      const { data: previousReceivablesData } = await supabase
        .from('transactions')
        .select('sum:amount::numeric')
        .eq('type', 'income')
        .eq('status', 'pending')
        .eq('user_id', userId)
        .gte('created_at', previousPeriodStart.toISOString())
        .lt('created_at', currentPeriodStart.toISOString());

      const currentReceivablesTotal = currentReceivablesData?.[0]?.sum || 0;
      const previousReceivablesTotal = previousReceivablesData?.[0]?.sum || 0;
      const receivablesTrend = previousReceivablesTotal > 0
        ? ((currentReceivablesTotal - previousReceivablesTotal) / previousReceivablesTotal) * 100
        : 0;

      // Load payables with trends
      const { data: currentPayablesData } = await supabase
        .from('transactions')
        .select('sum:amount::numeric')
        .eq('type', 'expense')
        .eq('status', 'pending')
        .eq('user_id', userId)
        .gte('created_at', currentPeriodStart.toISOString());

      const { data: previousPayablesData } = await supabase
        .from('transactions')
        .select('sum:amount::numeric')
        .eq('type', 'expense')
        .eq('status', 'pending')
        .eq('user_id', userId)
        .gte('created_at', previousPeriodStart.toISOString())
        .lt('created_at', currentPeriodStart.toISOString());

      const currentPayablesTotal = currentPayablesData?.[0]?.sum || 0;
      const previousPayablesTotal = previousPayablesData?.[0]?.sum || 0;
      const payablesTrend = previousPayablesTotal > 0
        ? ((currentPayablesTotal - previousPayablesTotal) / previousPayablesTotal) * 100
        : 0;

      // Calculate balance and trend
      const currentBalance = currentSalesTotal - currentPayablesTotal;
      const previousBalance = previousSalesTotal - previousPayablesTotal;
      const balanceTrend = previousBalance !== 0
        ? ((currentBalance - previousBalance) / Math.abs(previousBalance)) * 100
        : 0;

      setIndicators([
        {
          id: '1',
          label: 'Total de Vendas',
          value: currentSalesTotal,
          trend: Number(salesTrend.toFixed(1)),
          currency: true
        },
        {
          id: '2',
          label: 'Contas a Receber',
          value: currentReceivablesTotal,
          trend: Number(receivablesTrend.toFixed(1)),
          currency: true
        },
        {
          id: '3',
          label: 'Contas a Pagar',
          value: currentPayablesTotal,
          trend: Number(payablesTrend.toFixed(1)),
          currency: true
        },
        {
          id: '4',
          label: 'Saldo em Caixa',
          value: currentBalance,
          trend: Number(balanceTrend.toFixed(1)),
          currency: true
        }
      ]);

      // Load sales chart data
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      const { data: salesByMonth } = await supabase
        .from('sales')
        .select('created_at, total')
        .eq('user_id', userId)
        .gte('created_at', sixMonthsAgo.toISOString())
        .order('created_at', { ascending: true });

      if (salesByMonth) {
        const salesChartData = processChartData(salesByMonth, 'total');
        setSalesData(salesChartData);
      }

      // Load financial chart data
      const { data: transactions } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .gte('created_at', sixMonthsAgo.toISOString())
        .order('created_at', { ascending: true });

      if (transactions) {
        const incomeData = transactions.filter(tx => tx.type === 'income').reduce((acc, tx) => {
          const month = new Date(tx.created_at).getMonth();
          acc[month] = (acc[month] || 0) + tx.amount;
          return acc;
        }, {});

        const expenseData = transactions.filter(tx => tx.type === 'expense').reduce((acc, tx) => {
          const month = new Date(tx.created_at).getMonth();
          acc[month] = (acc[month] || 0) + tx.amount;
          return acc;
        }, {});

        setFinancialData({
          labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
          datasets: [
            {
              label: 'Receitas',
              data: Object.values(incomeData || {}),
              backgroundColor: 'rgba(16, 185, 129, 0.7)',
              borderColor: 'rgb(16, 185, 129)',
              borderWidth: 2
            },
            {
              label: 'Despesas',
              data: Object.values(expenseData || {}),
              backgroundColor: 'rgba(239, 68, 68, 0.7)',
              borderColor: 'rgb(239, 68, 68)',
              borderWidth: 2
            }
          ]
        });
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const processChartData = (data: any[], valueKey: string) => {
    const monthlyData = data.reduce((acc, item) => {
      const month = new Date(item.created_at).getMonth();
      acc[month] = (acc[month] || 0) + (item[valueKey] || 0);
      return acc;
    }, {});

    return {
      labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
      datasets: [{
        label: 'Vendas',
        data: Object.values(monthlyData),
        borderColor: 'rgb(37, 99, 235)',
        backgroundColor: 'rgba(37, 99, 235, 0.7)',
        borderWidth: 3,
        fill: true
      }]
    };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader />
      
      <main className="p-6">
        <div className="max-w-7xl mx-auto space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Ações Rápidas
            </h2>
            <QuickActions />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Indicadores Principais
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {indicators.map(indicator => (
                <IndicatorCard 
                  key={indicator.id}
                  indicator={indicator}
                />
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {salesData && (
              <ChartContainer
                title="Vendas por Período"
                type="line"
                data={salesData}
                height={360}
              />
            )}
            {financialData && (
              <ChartContainer
                title="Receitas vs Despesas"
                type="bar"
                data={financialData}
                height={360}
              />
            )}
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <RecentActivities activities={[]} />
            <AlertsPanel
              alerts={[]}
              onMarkAsRead={() => {}}
            />
          </section>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;